import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { SetupStateBadge } from "./StatusBadges";
import "./HealthRoutes.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

const sampleHistory = JSON.stringify({
  records: [
    {
      project_name: "APMS Release 1",
      sprint_name: "Sprint 01",
      planned_start: "2026-01-01",
      planned_end: "2026-01-14",
      actual_start: "2026-01-01",
      actual_end: "2026-01-16",
      planned_story_points: 42,
      completed_story_points: 37,
      team_capacity_hours: 360,
      defect_count: 8,
      scope_change_count: 3,
      morale_score: 76
    }
  ]
}, null, 2);

function moduleModeNeedsSetup(mode = "") {
  const normalized = mode.toLowerCase();
  return normalized.includes("mock") || normalized.includes("demo") || normalized.includes("simulated") || normalized.includes("waiting");
}

function useAuthedResource(endpoint, auth) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}${endpoint}`, { headers: authHeaders(auth.token) });
      setData(response.data);
      setNotice("Updated successfully.");
    } catch (err) {
      setNotice("Unable to load this module. Please refresh or check permissions.");
    } finally {
      setLoading(false);
    }
  }, [endpoint, auth.token]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, notice, load };
}

export const ApiModulePage = ({ auth, title, endpoint, actionLabel }) => {
  const resource = useAuthedResource(endpoint, auth);
  const mode = resource.data?.mode || "";
  return (
    <div className="reference-page projects-reference single" data-testid={`module-page-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <section className="ref-card settings-panel">
        <div className="projects-tab-row">
          <h2 data-testid={`module-title-${title.toLowerCase().replaceAll(" ", "-")}`}>{title}</h2>
          <div className="approval-actions">
            {moduleModeNeedsSetup(mode) && <SetupStateBadge testId={`module-setup-required-${title.toLowerCase().replaceAll(" ", "-")}`} label={mode.includes("SIMULATED") ? "SIMULATED / REPLACEABLE" : "MOCKED / SETUP REQUIRED"} />}
            <button className="primary-button compact" onClick={resource.load} data-testid={`refresh-${title.toLowerCase().replaceAll(" ", "-")}-button`}>{resource.loading ? "Loading..." : actionLabel}</button>
          </div>
        </div>
        {resource.notice && <p className="notice" data-testid="module-notice">{resource.notice}</p>}
        <pre className="module-json" data-testid="module-json-output">{JSON.stringify(resource.data || {}, null, 2)}</pre>
      </section>
    </div>
  );
};

export const VoiceCommandPage = ({ auth }) => {
  const [text, setText] = useState("Plan next sprint and show risks");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);
  const startVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setNotice("Browser voice recognition is not available here. Use typed voice command simulation instead.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.onresult = (event) => setText(event.results[0][0].transcript);
    recognition.start();
    setNotice("Listening for voice command...");
  };
  const processVoice = async () => {
    try {
      const response = await axios.post(`${API}/voice/command`, { command: text }, { headers: authHeaders(auth.token) });
      setResult(response.data);
      setNotice(`Voice command processed: ${response.data.action}`);
    } catch (err) {
      setNotice("Voice command API returned a controlled fallback. Try a typed command.");
    }
  };
  return <div className="reference-page projects-reference single" data-testid="voice-command-page"><section className="ref-card ask-agent-panel"><div className="projects-tab-row"><h2 data-testid="voice-command-title">Voice-driven Project Management</h2><SetupStateBadge testId="voice-browser-setup-badge" label="BROWSER VOICE / API READY" /></div><textarea className="command-input reference-command" value={text} onChange={(e) => setText(e.target.value)} data-testid="voice-command-textarea" /><div className="approval-actions"><button className="primary-button" onClick={startVoice} data-testid="start-voice-command-button">Start Voice</button><button className="secondary-button" onClick={processVoice} data-testid="process-voice-command-button">Process command</button><Link className="secondary-button" to="/agents/ask" data-testid="send-voice-to-agent-link">Send to Ask Agent</Link></div>{notice && <p className="notice" data-testid="voice-command-notice">{notice}</p>}{result && <pre className="module-json" data-testid="voice-command-result">{JSON.stringify(result, null, 2)}</pre>}</section></div>;
};

export const DigitalTwinPage = ({ auth }) => {
  const [status, setStatus] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [historyJson, setHistoryJson] = useState(sampleHistory);
  const [notice, setNotice] = useState("");

  const loadStatus = useCallback(async () => {
    const response = await axios.get(`${API}/digital-twin/calibration-status`, { headers: authHeaders(auth.token) });
    setStatus(response.data);
  }, [auth.token]);

  useEffect(() => { loadStatus().catch(() => setNotice("Digital Twin calibration status unavailable.")); }, [loadStatus]);

  const importHistory = async () => {
    try {
      const payload = JSON.parse(historyJson);
      const response = await axios.post(`${API}/digital-twin/history`, payload, { headers: authHeaders(auth.token) });
      setStatus(response.data.calibration);
      setNotice(`Imported ${response.data.imported} historical sprint record(s).`);
    } catch (err) {
      setNotice("Historical data import failed. Use the JSON template and real sprint records.");
    }
  };

  const runSimulation = async () => {
    try {
      const response = await axios.post(`${API}/digital-twin/simulate`, { scenario_name: "Historical calibration scenario", capacity_delta: 1, scope_delta: -5 }, { headers: authHeaders(auth.token) });
      setSimulation(response.data);
      setNotice(response.data.mode === "CALIBRATED_SIMULATION" ? "Calibrated simulation generated from historical data." : "Simulation remains uncalibrated until enough real data is available.");
    } catch (err) {
      setNotice("Digital Twin simulation could not run.");
    }
  };

  const resetHistory = async () => {
    try {
      const response = await axios.post(`${API}/digital-twin/history/reset`, {}, { headers: authHeaders(auth.token) });
      setStatus(response.data.calibration);
      setSimulation(null);
      setNotice(`Digital Twin demo history reset. Deleted ${response.data.deleted} record(s).`);
    } catch (err) {
      setNotice("Digital Twin reset requires Admin or Project Manager access.");
    }
  };

  return (
    <div className="reference-page projects-reference single" data-testid="digital-twin-page">
      <section className="workflow-hero" data-testid="digital-twin-hero">
        <div>
          <span className="section-kicker" data-testid="digital-twin-kicker">Digital Twin</span>
          <h2 data-testid="digital-twin-title">Calibration waits for real delivery history.</h2>
          <p data-testid="digital-twin-description">Import completed sprint data to activate calibrated predictions. Until then APMS keeps Digital Twin output clearly marked as simulated.</p>
        </div>
        <div className="health-summary-grid" data-testid="digital-twin-status-grid">
          <article className="health-summary-card tone-success" data-testid="digital-twin-record-count-card"><strong data-testid="digital-twin-record-count">{status?.record_count || 0}</strong><span>Records</span></article>
          <article className="health-summary-card tone-warning" data-testid="digital-twin-calibrated-card"><strong data-testid="digital-twin-calibrated-value">{status?.calibrated ? "ON" : "OFF"}</strong><span>Calibrated</span></article>
        </div>
      </section>
      {notice && <p className="notice" data-testid="digital-twin-notice">{notice}</p>}
      <section className="content-grid one-one" data-testid="digital-twin-workspace-grid">
        <section className="ref-card settings-panel" data-testid="digital-twin-history-import-panel">
          <div className="projects-tab-row"><h2 data-testid="digital-twin-import-title">Historical sprint data</h2><SetupStateBadge testId="digital-twin-data-required-badge" label={status?.calibrated ? "CALIBRATED" : "HISTORICAL DATA REQUIRED"} /></div>
          <textarea className="command-input reference-command" value={historyJson} onChange={(event) => setHistoryJson(event.target.value)} data-testid="digital-twin-history-json-input" />
          <div className="approval-actions"><button className="primary-button" onClick={importHistory} data-testid="import-digital-twin-history-button">Import</button><button className="secondary-button" onClick={runSimulation} data-testid="run-digital-twin-calibrated-simulation-button"><RefreshCw size={16} /> Simulate</button><button className="secondary-button" onClick={resetHistory} data-testid="reset-digital-twin-history-button">Reset</button></div>
        </section>
        <section className="ref-card settings-panel" data-testid="digital-twin-calibration-status-panel">
          <div className="projects-tab-row"><h2 data-testid="digital-twin-status-title">Calibration status</h2><button className="secondary-button compact" onClick={loadStatus} data-testid="refresh-digital-twin-status-button">Refresh</button></div>
          <pre className="module-json" data-testid="digital-twin-status-json">{JSON.stringify(status || {}, null, 2)}</pre>
          {simulation && <pre className="module-json" data-testid="digital-twin-simulation-json">{JSON.stringify(simulation, null, 2)}</pre>}
        </section>
      </section>
    </div>
  );
};